import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import api from '../../../api';

// ----------------------------------------------------------------------

/**
 * Catches up every result that is still undeclared for today, in one go.
 *
 * Two steps on purpose. The first only looks: it reads the results source and
 * lists what it would settle. The second actually settles it, and that pays
 * winners immediately and cannot be undone — so the list has to be seen before
 * the button that acts on it appears.
 */
export default function AutoDeclarePanel({ onDeclared }) {
  const [checking, setChecking] = useState(false);
  const [declaring, setDeclaring] = useState(false);
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const check = async () => {
    setChecking(true);
    setError('');
    setResult(null);
    setReport(null);

    try {
      const res = await api.post('/auto-results/run', { dry_run: true });
      setReport(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not read the results source');
    } finally {
      setChecking(false);
    }
  };

  const declareAll = async () => {
    setDeclaring(true);
    setError('');

    try {
      const res = await api.post('/auto-results/run', { skip_waiting: true });
      setResult(res.data);
      setReport(null);
      if (onDeclared) onDeclared();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not declare');
    } finally {
      setDeclaring(false);
    }
  };

  const pending = report?.would_declare || [];
  const conflicts = report?.conflicts || result?.conflicts || [];
  const declared = result?.declared || [];
  const skipped = report?.skipped || result?.skipped || [];
  const summary = report || result;

  // "Nothing is waiting" on its own is not an answer — it leaves you wondering
  // whether a market was already settled, has no pending result, or never
  // matched the source at all. Every reason the sweep recorded is shown.
  const skippedByReason = skipped.reduce((acc, s) => {
    const key = s.reason || 'skipped';
    if (!acc[key]) acc[key] = [];
    if (s.game) acc[key].push(s.session ? `${s.game} (${s.session})` : s.game);
    return acc;
  }, {});

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Declare today&apos;s pending results
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Reads today&apos;s declared results from the results source and settles every session you
        have not declared yet — morning ones you missed as well as this evening&apos;s. Only
        markets whose own session time has already passed are touched, and the pana, ank and jodi
        have to agree with each other before anything is settled.
      </Typography>

      <Stack direction="row" spacing={2}>
        <LoadingButton variant="outlined" loading={checking} onClick={check}>
          Check what&apos;s pending
        </LoadingButton>

        {pending.length > 0 && (
          <LoadingButton variant="contained" color="warning" loading={declaring} onClick={declareAll}>
            Declare these {pending.length} now
          </LoadingButton>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {report && pending.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nothing is ready to declare right now.
        </Alert>
      )}

      {summary && (
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
          Source had {summary.candidates} markets; {summary.matched} matched one of your games.
        </Typography>
      )}

      {Object.keys(skippedByReason).length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Not declared, and why
          </Typography>
          {Object.entries(skippedByReason).map(([reason, list]) => (
            <Typography
              key={reason}
              variant="body2"
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              <b>{reason}</b>
              {list.length ? `: ${list.join(', ')}` : ''}
            </Typography>
          ))}
        </Box>
      )}

      {summary && summary.matched === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          None of the markets on the source matched a game of yours by name. Set{' '}
          <b>result_source_name</b> on the game to whatever the source calls it.
        </Alert>
      )}

      {pending.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            These {pending.length} would be declared. Winners are paid immediately and this cannot
            be undone — check the numbers against the market before you continue.
          </Alert>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Game</TableCell>
                  <TableCell>Session</TableCell>
                  <TableCell>Pana - Ank</TableCell>
                  <TableCell>From market</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((row) => (
                  <TableRow key={`${row.game}-${row.session}`}>
                    <TableCell>{row.game}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.session} />
                    </TableCell>
                    <TableCell>
                      <b>
                        {row.panna} - {row.digit}
                      </b>
                    </TableCell>
                    <TableCell>{row.source_market}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {declared.length > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Declared {declared.length}:{' '}
          {declared.map((d) => `${d.game} ${d.session} ${d.value} (${d.winners} winners)`).join(', ')}
        </Alert>
      )}

      {result && declared.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nothing was declared.
        </Alert>
      )}

      {conflicts.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Held back for you to decide by hand — the source disagreed with itself or the numbers did
          not add up:
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {conflicts.map((c) => (
              <li key={`${c.game}-${c.session || ''}`}>
                {c.game} {c.session || ''} — {(c.problems || []).join('; ')}
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </Card>
  );
}
